import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  TextField,
  Button,
  IconButton,
  Grid,
  MenuItem,
  Select,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useState, useEffect } from "react";
import { createSmartfloUser } from "../../Services/SmartFloApis";
import { getSmartfloNumbers } from "../../Services/SmartFloApis";
import { InputAdornment } from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";

export default function CreateUserModal({ open, onClose, manager, onSuccess }) {
  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    password: "",
  });

  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [numbers, setNumbers] = useState([]);
  const [selectedCallerId, setSelectedCallerId] = useState("");

  useEffect(() => {
    if (manager) {
      const [first, last] = manager.name?.split(" ") || [];
      setForm({
        firstName: first || "",
        lastName: last || "",
        email: manager.email || "",
        phone: "",
        password: "",
      });
      setSelectedCallerId("");
      setShowPassword(false);
    }
  }, [manager]);

  useEffect(() => {
    if (open) {
      fetchNumbers();
    }
  }, [open]);

  const fetchNumbers = async () => {
    try {
      const res = await getSmartfloNumbers();
      console.log("my numbers", res.data);
      setNumbers(res.data || []);
    } catch (err) {
      console.error("❌ Failed to fetch numbers");
    }
  };

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);

      if (!form.firstName?.trim()) {
        alert("Please enter First Name");
        return;
      }

      if (!form.lastName?.trim()) {
        alert("Please enter Last Name");
        return;
      }

      if (!form.email?.trim()) {
        alert("Please enter Email");
        return;
      }

      if (!form.phone?.trim()) {
        alert("Please enter Phone Number");
        return;
      }

      if (!form.password?.trim()) {
        alert("Please enter Password");
        return;
      }

      if (!selectedCallerId) {
        alert("Please select Caller ID");
        return;
      }

      console.log("Selected Caller ID:", selectedCallerId);

      const selectedNumber = numbers.find((n) => n.id === selectedCallerId);

      if (!selectedNumber) {
        alert("Invalid Caller ID selected");
        return;
      }

      const payload = {
        name: `${form.firstName} ${form.lastName}`,
        number: form.phone,
        email: form.email,
        login_id: form.email,
        password: form.password,
        userId: manager?.id,
        caller_id: [selectedCallerId],
        caller_did: selectedNumber?.did || "",
      };

      console.log("🚀 Payload:", payload);

      const res = await createSmartfloUser(payload);

      console.log("✅ Response:", res);

      if (res.success) {
        onSuccess(manager?.id, res.smartfloUserId);
        onClose();
      } else {
        alert(res.error || "Failed to create user");
      }
    } catch (error) {
      console.error("❌ Error:", error);
      alert(error.message || "Error creating SmartFlo user");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogContent sx={{ p: 3, borderRadius: 3 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
          <Box>
            <Typography fontWeight={600} fontSize={18}>
              Create SmartFlo Account
            </Typography>
            <Typography fontSize={13} color="#6b7280">
              Fill in the details to create a SmartFlo user for{" "}
              <b>{manager?.name}</b>
            </Typography>
          </Box>

          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>

        <Grid container spacing={2}>
          <Grid item xs={6}>
            <Box display="flex" alignItems="center" gap={0.5}>
              <Typography fontSize={13}>First Name </Typography>
              <span style={{ color: "red" }}>*</span>
            </Box>
            <TextField
              fullWidth
              size="small"
              value={form.firstName}
              onChange={(e) => handleChange("firstName", e.target.value)}
            />
          </Grid>

          <Grid item xs={6}>
            <Box display="flex" alignItems="center" gap={0.5}>
              <Typography fontSize={13}>Last Name </Typography>
              <span style={{ color: "red" }}>*</span>
            </Box>
            <TextField
              fullWidth
              size="small"
              value={form.lastName}
              onChange={(e) => handleChange("lastName", e.target.value)}
            />
          </Grid>

          <Grid item xs={12}>
            <Box display="flex" alignItems="center" gap={0.5}>
              <Typography fontSize={13}>Email </Typography>
              <span style={{ color: "red" }}>*</span>
            </Box>
            <TextField
              fullWidth
              size="small"
              value={form.email}
              onChange={(e) => handleChange("email", e.target.value)}
            />
          </Grid>

          <Grid item xs={6}>
            <Box display="flex" alignItems="center" gap={0.5}>
              <Typography fontSize={13}>Phone Number </Typography>
              <span style={{ color: "red" }}>*</span>
            </Box>
            <TextField
              fullWidth
              size="small"
              value={form.phone}
              onChange={(e) => handleChange("phone", e.target.value)}
              placeholder="+91 9876543210"
            />
          </Grid>

          <Grid item xs={6}>
            <Box display="flex" alignItems="center" gap={0.5}>
              <Typography fontSize={13}>Password </Typography>
              <span style={{ color: "red" }}>*</span>
            </Box>
            <TextField
              fullWidth
              size="small"
              type={showPassword ? "text" : "password"}
              value={form.password}
              onChange={(e) => handleChange("password", e.target.value)}
              placeholder="e.g. Vjsb@1234"
              helperText="Min 8 chars with 1 uppercase, 1 lowercase and 1 special character (Tata requirement)"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword((prev) => !prev)}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Grid>

          <Grid item xs={6}>
            <Box display="flex" alignItems="center" gap={0.5}>
              <Typography fontSize={13}>Caller ID</Typography>
              <span style={{ color: "red" }}>*</span>
            </Box>

            <TextField
              select
              fullWidth
              size="small"
              value={selectedCallerId}
              onChange={(e) => setSelectedCallerId(e.target.value)}
              SelectProps={{
                displayEmpty: true,
                MenuProps: {
                  PaperProps: {
                    sx: {
                      maxHeight: 220,
                      borderRadius: "2px !important",
                      boxShadow: "0px 6px 16px rgba(0,0,0,0.12)",
                    },
                  },
                },
                renderValue: (selected) => {
                  if (!selected) return "Select Caller ID";
                  const item = numbers.find((n) => n.id === selected);
                  return item ? item.did : selected;
                },
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "10px",
                  backgroundColor: "#fff",
                },
              }}
            >
              {numbers.map((num) => (
                <MenuItem
                  key={num.id}
                  value={num.id}
                  sx={{
                    fontFamily: "Inter-medium",
                    fontSize: "14px",
                  }}
                >
                  {num.did}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
        </Grid>

        <Box
          sx={{ display: "flex", justifyContent: "flex-end", gap: 2, mt: 3 }}
        >
          <Button
            variant="outlined"
            onClick={onClose}
            sx={{ textTransform: "none" }}
          >
            Cancel
          </Button>

          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={loading}
            sx={{
              textTransform: "none",
              bgcolor: "#2563eb",
              px: 2.5,
            }}
          >
            {loading ? "Creating..." : "Create SmartFlo Account"}
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
}